import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { facilities, pillars } from "@/mocks/about";

const eyebrow = "text-[14px] tracking-[0.25em] uppercase text-primary-600 mb-3";
const h2 =
  "font-heading text-3xl md:text-5xl !leading-[1.3] text-foreground-950 mb-5";
const btnOutline =
  "inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-foreground-900 text-xs tracking-widest uppercase whitespace-nowrap cursor-pointer hover:bg-foreground-950 hover:text-background-50 transition-colors";

type Pillar = { title: string; desc: string };
type Facility = { title: string };
type Stat = { value: string; label: string };

const valueIcons = [
  "ri-award-line",
  "ri-seedling-line",
  "ri-shield-check-line",
  "ri-hand-heart-line",
  "ri-user-heart-line",
];

export function AboutHero() {
  const { t } = useTranslation();
  return (
    <section className="relative h-[420px] md:h-[520px] w-full overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Vast%20Mekong%20Delta%20rice%20paddy%20landscape%20in%20southern%20Vietnam%20beneath%20a%20bright%20clear%20sky-blue%20sky%2C%20a%20gentle%20winding%20river%20flowing%20through%20emerald%20rice%20fields%20mirroring%20the%20light%20blue%20sky%2C%20a%20flock%20of%20birds%20soaring%20across%20the%20luminous%20blue%20horizon%2C%20thin%20soft%20haze%20floating%20over%20the%20paddies%2C%20fresh%20light%20sky-blue%20and%20azure%20color%20palette%2C%20bright%20airy%20landscape%20photography&width=1920&height=700&seq=lam-about-hero&orientation=landscape&nocache=true"
        alt="Mekong Delta rice fields with river"
        title="About EverRice"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-background-50/40 via-background-50/15 to-transparent" />
      <div className="relative h-full flex items-center page-gutter">
        <div className="max-w-xl fade-up pt-16">
          <p className="font-heading text-lg tracking-[0.3em] text-foreground-700 mb-4">
            {t("aboutPage.hero.eyebrow")}
          </p>
          <h1 className="font-heading text-4xl md:text-[3.25rem] !leading-[1.3] tracking-[0.03em] text-foreground-950 mb-6">
            {t("aboutPage.hero.title")}
          </h1>
          <p className="text-sm md:text-base text-foreground-800 leading-relaxed">
            {t("aboutPage.hero.desc")}
          </p>
        </div>
      </div>
    </section>
  );
}

export function OurStory() {
  const { t } = useTranslation();
  return (
    <section className="bg-background-50 page-gutter py-16 md:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className={eyebrow}>{t("aboutPage.story.eyebrow")}</p>
          <h2 className={h2}>{t("aboutPage.story.title")}</h2>
          <div className="space-y-4 text-sm text-foreground-700 leading-relaxed max-w-lg">
            <p>{t("aboutPage.story.p1")}</p>
            <p>{t("aboutPage.story.p2")}</p>
          </div>
          <div className="flex flex-wrap gap-4 mt-8">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-xs tracking-widest uppercase whitespace-nowrap cursor-pointer"
            >
              {t("aboutPage.story.ctaProducts")}{" "}
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/contact" className={btnOutline}>
              {t("aboutPage.story.ctaContact")} <i className="ri-mail-line" />
            </Link>
          </div>
        </div>
        <div className="w-full h-72 md:h-[440px] rounded-lg overflow-hidden">
          <img
            src="img/about_bg.jpg"
            alt="Top-down aerial view of EverRice rice export factory and processing plant"
            title="Rice export factory top-down aerial view - EverRice"
            className="w-full h-full object-cover object-top"
          />
        </div>
      </div>
    </section>
  );
}

export function Pillars() {
  const { t } = useTranslation();
  const items = t("aboutPage.pillars.items", {
    returnObjects: true,
  }) as unknown as Pillar[];
  return (
    <section className="bg-primary-50 page-gutter py-16 md:py-24">
      <div className="text-center mb-12">
        <p className={eyebrow}>{t("aboutPage.pillars.eyebrow")}</p>
        <h2 className={`${h2} mb-0`}>{t("aboutPage.pillars.title")}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {items.map((p, i) => (
          <div
            key={p.title}
            className={`rounded-lg p-8 ${i === 1 ? "bg-primary-800 text-background-50" : "bg-background-50 border border-background-200"}`}
          >
            <div
              className={`w-14 h-14 rounded-full flex items-center justify-center mb-5 ${i === 1 ? "bg-background-50/20" : "bg-primary-100"}`}
            >
              <i
                className={`${pillars[i].icon} text-2xl ${i === 1 ? "text-background-50" : "text-primary-600"}`}
              />
            </div>
            <h3 className="font-heading text-2xl mb-3">{p.title}</h3>
            <p
              className={`text-sm leading-relaxed ${i === 1 ? "text-background-200" : "opacity-90"}`}
            >
              {p.desc}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function Values() {
  const { t } = useTranslation();
  const items = t("aboutPage.values.items", {
    returnObjects: true,
  }) as unknown as Pillar[];
  return (
    <section className="bg-background-50 page-gutter py-16 md:py-24">
      <div className="text-center mb-12">
        <p className={eyebrow}>{t("aboutPage.values.eyebrow")}</p>
        <h2 className={`${h2} mb-0`}>{t("aboutPage.values.title")}</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
        {items.map((m, i) => (
          <div
            key={m.title}
            className="group rounded-lg bg-gradient-to-br from-primary-300 via-primary-500 to-accent-400 p-px shadow-sm transition-shadow hover:shadow-md"
          >
            <div className="h-full rounded-[7px] bg-background-50 p-6">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500 mb-4">
                <i className={`${valueIcons[i]} text-xl text-background-50`} />
              </div>
              <h3 className="font-heading text-lg mb-2 text-foreground-950">
                {m.title}
              </h3>
              <p className="text-xs text-foreground-600 leading-relaxed">
                {m.desc}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function Facilities() {
  const { t } = useTranslation();
  const items = t("aboutPage.facilities.items", {
    returnObjects: true,
  }) as unknown as Facility[];
  return (
    <section className="bg-primary-50 page-gutter py-16 md:py-24">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
        <div>
          <p className={eyebrow}>{t("aboutPage.facilities.eyebrow")}</p>
          <h2 className={`${h2} mb-0`}>{t("aboutPage.facilities.title")}</h2>
        </div>
        <p className="text-sm text-foreground-700 max-w-sm leading-relaxed">
          {t("aboutPage.facilities.sub")}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {items.map((f, i) => (
          <div
            key={f.title}
            className="rounded-lg overflow-hidden border border-background-200 bg-background-50 group cursor-pointer"
          >
            <div className="w-full h-56 overflow-hidden">
              <img
                src={facilities[i].img}
                alt={f.title}
                title={`${f.title} - EverRice`}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-5">
              <h3 className="font-heading text-xl">{f.title}</h3>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function Numbers() {
  const { t } = useTranslation();
  const stats = t("aboutPage.stats", {
    returnObjects: true,
  }) as unknown as Stat[];
  return (
    <section className="bg-background-50 page-gutter py-16 md:py-24">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((s, i) => (
          <div
            key={s.label}
            className={`group rounded-lg p-px shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md ${i === 1 ? "bg-primary-500" : "bg-gradient-to-br from-primary-300 via-primary-500 to-accent-400"}`}
          >
            <div
              className={`h-full rounded-[7px] p-6 text-center ${i === 1 ? "bg-primary-500 text-background-50" : "bg-background-50"}`}
            >
              <p
                className={`font-heading text-4xl md:text-5xl ${i === 1 ? "text-background-50" : "text-foreground-950"}`}
              >
                {s.value}
              </p>
              <p
                className={`text-xs tracking-widest uppercase mt-2 ${i === 1 ? "text-background-100" : "text-foreground-800"}`}
              >
                {s.label}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
